import { HashLink } from "react-router-hash-link";
import bbq from "./bbq.jpeg";
import tanks from "./tanks.png";
import dives from "./dives.jpeg";
import diver from "./diver.jpeg";
import dolphin from "./dolphin.jpeg";
import equipment from "./equipment.webp";
import fishing from "./fishing.webp";
import fishingMorning from "./fishingmorning.jpeg";
import moocho from "./moocho.jpeg";
import sajee from "./sajee.jpeg";
import mantas from "./mantas.jpeg";
import styles from "./MyDive.module.css";

import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";

const ACTIVITIES = [
  {
    image: dives,
    title: "Fun dives",
    body: "Dive into excitement with our thrilling adventures — encounters with majestic sharks and gentle mantas, the mystery of night dives and the allure of wreck exploration.",
  },
  {
    image: equipment,
    title: "Equipment",
    body: "Dive worry-free. Rent top-quality gear from us and explore the depths with confidence — BCDs, regs, wetsuits, masks, fins and 15L tanks.",
  },
  {
    image: diver,
    title: "Dive courses",
    body: "Learn the basics from our expert instructors with our beginner course, or take our advanced course to master new techniques and explore deeper realms.",
  },
  {
    image: dolphin,
    title: "Dolphin cruise",
    body: "Experience the enchantment of a Maldivian sunset while spotting playful pods of spinner dolphins. Two hours, mostly silence, and the ocean.",
  },
  {
    image: fishing,
    title: "Night fishing",
    body: "Cast your line under the starlit sky and reel in unforgettable memories with our night fishing excursions — bring your appetite.",
  },
  {
    image: fishingMorning,
    title: "Sunrise trolling",
    body: "Start your day with adventure. Sunrise trolling in pristine waters — tuna, wahoo, sailfish, mahi-mahi. Caught at dawn, eaten by lunch.",
  },
];

const TEAM = [
  {
    image: moocho,
    name: "Moocho",
    role: "Captain & dive guide",
    bio: "Yo-ho! I'm Moocho, a captain and diver for two decades, exploring the Maldives' depths year-round. From coral to currents, I navigate seasonal shifts and animal migrations, dedicated to preserving our marine wonders for future generations. Join me as we embark on a voyage of conservation and discovery.",
  },
  {
    image: sajee,
    name: "Sajee",
    role: "Dive guide & explorer",
    bio: "Hello, fellow adventurers! I'm Sajee, a dive aficionado with a penchant for exploration. With fins on my feet and a sense of wonder in my heart, I've traversed the depths of both the northern and southern realms of the Maldives, uncovering hidden treasures beneath the waves.",
  },
];

const SECTION_LINKS = [
  { label: "About", to: "#about" },
  { label: "Activities", to: "#activities" },
  { label: "Team", to: "#team" },
];

export const MyDive = () => {
  return (
    <div className={styles.page} id="top">
      <Header transparent />

      {/* ===================== HERO ===================== */}
      <section className={styles.hero}>
        <img src={mantas} className={styles.heroImg} alt="" />
        <div className={styles.heroOverlay} aria-hidden="true" />
        <div className={styles.heroContent}>
          <span className={styles.heroEyebrow}>MY Adventures · Dive Centre</span>
          <h1 className={styles.heroTitle}>My Dive</h1>
          <p className={styles.heroLede}>
            A floating dive centre in the heart of the Maldives. Daily fun
            dives, full PADI courses, and a crew that's spent a lifetime
            with these reefs.
          </p>
        </div>
        <nav className={styles.heroNav} aria-label="Sections">
          {SECTION_LINKS.map((link) => (
            <HashLink
              key={link.label}
              smooth
              to={link.to}
              className={styles.heroNavLink}
            >
              {link.label}
            </HashLink>
          ))}
        </nav>
      </section>

      {/* ===================== ABOUT ===================== */}
      <section id="about" className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHead}>
            <span className="eyebrow">About us</span>
            <h2 className={styles.sectionTitle}>
              Maldives' favourite floating dive centre.
            </h2>
          </div>
          <div className={styles.aboutGrid}>
            <div className={styles.aboutImage}>
              <img src={tanks} alt="Divers with tanks" />
            </div>
            <div className={styles.aboutCopy}>
              <p>
                Welcome to MY Adventures. Immerse yourself in unparalleled
                underwater experiences from our unique platform on the
                water — no long boat rides, no rushed transfers; you're
                at the dive site in minutes.
              </p>
              <p>
                With daily dive trips and expert PADI courses, our
                certified team makes sure every adventure beneath the
                waves is unforgettable. Discover the magic of the
                Maldives with us, where every dive is a journey into
                paradise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== ACTIVITIES ===================== */}
      <section id="activities" className={`${styles.section} ${styles.sectionAlt}`}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHead}>
            <span className="eyebrow">Activities</span>
            <h2 className={styles.sectionTitle}>What awaits you.</h2>
          </div>
          <div className={styles.activityGrid}>
            {ACTIVITIES.map((a) => (
              <article key={a.title} className={styles.activity}>
                <div className={styles.activityImage}>
                  <img src={a.image} alt="" loading="lazy" />
                </div>
                <div className={styles.activityBody}>
                  <h3 className={styles.activityTitle}>{a.title}</h3>
                  <p className={styles.activityDesc}>{a.body}</p>
                </div>
              </article>
            ))}
          </div>

          <article className={styles.bbqCallout}>
            <div className={styles.bbqImage}>
              <img src={bbq} alt="" loading="lazy" />
            </div>
            <div className={styles.bbqBody}>
              <span className="eyebrow">Onshore</span>
              <h3 className={styles.bbqTitle}>Sandbank beach barbecue.</h3>
              <p className={styles.bbqDesc}>
                Pull up to a private sandbank, light the grill, watch the
                stars come out. Indulge in delicious grilled delights
                while soaking in stunning ocean views — the perfect way
                to end a dive week.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* ===================== TEAM ===================== */}
      <section id="team" className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHead}>
            <span className="eyebrow">The team</span>
            <h2 className={styles.sectionTitle}>The faces behind the boat.</h2>
          </div>
          <div className={styles.teamGrid}>
            {TEAM.map((t) => (
              <article key={t.name} className={styles.teamMember}>
                <div className={styles.teamPortrait}>
                  <img src={t.image} alt={t.name} loading="lazy" />
                </div>
                <div className={styles.teamBody}>
                  <span className={styles.teamRole}>{t.role}</span>
                  <h3 className={styles.teamName}>{t.name}</h3>
                  <p className={styles.teamBio}>{t.bio}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
